import React, { useMemo, useState } from 'react';
import { isEnglishPresentation, localizedServerContent, localizedValue } from '../../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

function panelShellStyle(open) {
  return {
    marginTop: 24,
    borderRadius: 30,
    border: open
      ? '1px solid rgba(247,215,116,0.36)'
      : '1px solid rgba(247,215,116,0.20)',
    background: open
      ? 'linear-gradient(180deg, rgba(44,18,76,0.96) 0%, rgba(17,10,33,0.98) 52%, rgba(11,8,22,0.99) 100%)'
      : 'linear-gradient(180deg, rgba(40,17,70,0.92) 0%, rgba(16,11,31,0.96) 100%)',
    boxShadow: open
      ? '0 34px 90px rgba(0,0,0,0.40), 0 0 0 1px rgba(247,215,116,0.09) inset, 0 0 42px rgba(138,43,226,0.14)'
      : '0 22px 56px rgba(0,0,0,0.28), 0 0 0 1px rgba(247,215,116,0.05) inset',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    overflow: 'hidden',
    transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease, transform 180ms ease',
    position: 'relative',
  };
}

function shellGlowStyle(position = 'topRight') {
  const shared = {
    position: 'absolute',
    pointerEvents: 'none',
    borderRadius: '50%',
    filter: 'blur(24px)',
    opacity: 0.92,
  };

  if (position === 'bottomLeft') {
    return {
      ...shared,
      left: -70,
      bottom: -70,
      width: 220,
      height: 220,
      background: 'radial-gradient(circle, rgba(138,43,226,0.30) 0%, rgba(138,43,226,0.05) 42%, rgba(138,43,226,0) 74%)',
    };
  }

  return {
    ...shared,
    right: -58,
    top: -54,
    width: 230,
    height: 230,
    background: 'radial-gradient(circle, rgba(247,215,116,0.24) 0%, rgba(247,215,116,0.08) 38%, rgba(247,215,116,0) 74%)',
  };
}

function collapsedButtonStyle(open, compact = false) {
  return {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: compact ? '1fr' : 'minmax(0, 1fr) auto',
    alignItems: compact ? 'stretch' : 'center',
    columnGap: 16,
    rowGap: compact ? 12 : 0,
    padding: compact ? '16px 16px 14px' : (open ? '18px 20px' : '17px 18px'),
    border: 'none',
    background: open
      ? 'linear-gradient(135deg, rgba(247,215,116,0.10), rgba(138,43,226,0.12))'
      : 'linear-gradient(135deg, rgba(247,215,116,0.08), rgba(138,43,226,0.10))',
    cursor: 'default',
    textAlign: 'left',
    position: 'relative',
    zIndex: 1,
  };
}

function collapsedLeftStyle(compact = false) {
  return {
    display: 'flex',
    alignItems: compact ? 'flex-start' : 'center',
    gap: compact ? 12 : 14,
    minWidth: 0,
  };
}

function agentOrbStyle(compact = false) {
  const size = compact ? 68 : 78;
  const inner = compact ? 64 : 74;

  return {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'transparent',
    boxShadow: '0 0 16px rgba(218,165,32,0.48), 0 0 42px rgba(138,43,226,0.34)',
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative',
    animation: 'kronosPulse 4.6s ease-in-out infinite',
    '--kronos-avatar-size': `${inner}px`,
  };
}

function agentAvatarImageStyle() {
  return {
    width: 'var(--kronos-avatar-size, 74px)',
    height: 'var(--kronos-avatar-size, 74px)',
    objectFit: 'contain',
    display: 'block',
    pointerEvents: 'none',
    userSelect: 'none',
  };
}

function kronosWatermarkStyle() {
  return {
    position: 'absolute',
    right: -14,
    bottom: -32,
    width: 190,
    height: 190,
    objectFit: 'contain',
    opacity: 0.07,
    filter: 'blur(0.2px)',
    pointerEvents: 'none',
    userSelect: 'none',
  };
}

function kronosInlineAvatarWrapStyle(size = 42) {
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'transparent',
    boxShadow: '0 0 14px rgba(218,165,32,0.38), 0 0 28px rgba(138,43,226,0.28)',
    overflow: 'hidden',
    flexShrink: 0,
  };
}

function kronosInlineAvatarImageStyle(size = 38) {
  return {
    width: size,
    height: size,
    objectFit: 'contain',
    display: 'block',
    pointerEvents: 'none',
    userSelect: 'none',
  };
}

function kronosResponseHeaderStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  };
}

function kronosIdentityRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minWidth: 0,
  };
}

function collapsedTitleWrapStyle(compact = false) {
  return {
    minWidth: 0,
    display: 'grid',
    gap: compact ? 4 : 6,
    flex: 1,
    alignContent: 'center',
  };
}

function agentBadgeStyle() {
  return {
    display: 'inline-flex',
    width: 'fit-content',
    maxWidth: 'fit-content',
    flexShrink: 0,
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#4b0082',
    border: '1px solid rgba(255,243,196,0.72)',
    background: 'linear-gradient(135deg, rgba(255,245,194,0.96), rgba(247,215,116,0.96))',
    boxShadow: '0 8px 20px rgba(218,165,32,0.22)',
    whiteSpace: 'nowrap',
  };
}



function collapsedRightStyle(compact = false) {
  return {
    display: 'flex',
    alignItems: compact ? 'flex-start' : 'center',
    gap: 10,
    flexShrink: 0,
    justifySelf: compact ? 'start' : 'end',
    flexWrap: 'wrap',
    width: compact ? '100%' : 'auto',
  };
}

function statPillStyle(primary = false, compact = false) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: compact ? 32 : 34,
    padding: compact ? '0 14px' : '0 12px',
    borderRadius: 999,
    fontSize: compact ? 11 : 12,
    fontWeight: 900,
    color: primary ? '#4b0082' : '#fff3c4',
    border: primary
      ? '1px solid rgba(255,243,196,0.78)'
      : '1px solid rgba(247,215,116,0.22)',
    background: primary
      ? 'linear-gradient(135deg, rgba(255,245,194,0.96), rgba(247,215,116,0.96))'
      : 'linear-gradient(135deg, rgba(138,43,226,0.22), rgba(75,0,130,0.28))',
    boxShadow: primary ? '0 8px 18px rgba(218,165,32,0.22)' : 'none',
    whiteSpace: 'nowrap',
  };
}


function contentWrapStyle() {
  return {
    padding: '8px 16px 18px',
    position: 'relative',
    zIndex: 1,
  };
}

function introBoxStyle() {
  return {
    marginTop: 8,
    marginBottom: 18,
    padding: 18,
    borderRadius: 22,
    border: '1px solid rgba(247,215,116,0.18)',
    background: 'linear-gradient(135deg, rgba(255,245,194,0.08), rgba(138,43,226,0.10))',
    boxShadow: '0 14px 28px rgba(0,0,0,0.18)',
    display: 'grid',
    gap: 10,
    position: 'relative',
    overflow: 'hidden',
  };
}

function introTitleStyle() {
  return {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#fff3c4',
  };
}

function introTextStyle() {
  return {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.82)',
  };
}

function gridStyle() {
  return {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 14,
  };
}

function cardStyle({ active = false, asked = false, disabled = false }) {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    minHeight: 142,
    textAlign: 'left',
    padding: '15px 15px 14px',
    borderRadius: 22,
    border: active
      ? '1px solid rgba(247,215,116,0.46)'
      : asked
        ? '1px solid rgba(255,243,196,0.16)'
        : '1px solid rgba(247,215,116,0.14)',
    background: active
      ? 'linear-gradient(135deg, rgba(247,215,116,0.16), rgba(138,43,226,0.18))'
      : 'linear-gradient(180deg, rgba(255,245,194,0.06), rgba(255,255,255,0.03))',
    boxShadow: active
      ? '0 18px 38px rgba(0,0,0,0.24), 0 0 28px rgba(247,215,116,0.08)'
      : '0 10px 24px rgba(0,0,0,0.12)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.58 : 1,
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, opacity 180ms ease, background 180ms ease',
  };
}

function categoryChipStyle() {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    borderRadius: 999,
    padding: '6px 11px',
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    color: '#4b0082',
    background: 'linear-gradient(135deg, rgba(255,245,194,0.98), rgba(247,215,116,0.98))',
    border: '1px solid rgba(255,243,196,0.68)',
    boxShadow: '0 8px 16px rgba(218,165,32,0.18)',
  };
}

function questionCardKronosAvatarWrapStyle() {
  return {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    background: 'radial-gradient(circle at 30% 30%, rgba(75,0,130,0.78) 0%, rgba(58,0,110,0.82) 48%, rgba(32,0,72,0.88) 100%)',
    boxShadow: '0 0 1px rgba(98,0,168,0.18)',
  };
}

function questionCardKronosAvatarImageStyle() {
  return {
    width: 28,
    height: 28,
    objectFit: 'contain',
    display: 'block',
    pointerEvents: 'none',
    userSelect: 'none',
  };
}

function questionTextStyle() {
  return {
    fontSize: 15,
    lineHeight: 1.55,
    color: '#fff3c4',
    fontWeight: 900,
  };
}

function helperTextStyle() {
  return {
    fontSize: 12.5,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.72)',
  };
}

function stateRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 'auto',
  };
}

function miniStateStyle(loading = false, active = false) {
  return {
    fontSize: 12,
    fontWeight: 900,
    color: loading
      ? '#f7d774'
      : active
        ? '#fff3c4'
        : 'rgba(255,255,255,0.76)',
  };
}

function interpretationCardStyle() {
  return {
    marginTop: 20,
    padding: 20,
    borderRadius: 24,
    border: '1px solid rgba(247,215,116,0.22)',
    background: 'linear-gradient(180deg, rgba(255,245,194,0.08), rgba(138,43,226,0.08), rgba(255,255,255,0.03))',
    boxShadow: '0 20px 46px rgba(0,0,0,0.22), 0 0 32px rgba(247,215,116,0.06)',
    display: 'grid',
    gap: 12,
  };
}

function interpretationEyebrowStyle() {
  return {
    fontSize: 10,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#f7d774',
    fontWeight: 900,
  };
}

function interpretationTitleStyle() {
  return {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.32,
    fontWeight: 900,
    color: '#fff3c4',
  };
}


function interpretationTextStyle() {
  return {
    margin: 0,
    whiteSpace: 'pre-wrap',
    fontSize: 15,
    lineHeight: 1.9,
    color: 'rgba(255,255,255,0.94)',
  };
}

function highlightedNameStyle() {
  return {
    color: '#f7d774',
    fontWeight: 900,
    textShadow: '0 0 10px rgba(247,215,116,0.12)',
  };
}

function renderHighlightedAnswerText(text, title = '') {
  const rawText = polishAnswerPreviewText(text || '');
  if (!rawText) return null;

  const candidateNames = [];
  const titleText = String(title || '').trim();

  const excludedWords = new Set([
    'Sol',
    'Luna',
    'Mercurio',
    'Venus',
    'Marte',
    'Júpiter',
    'Saturno',
    'Urano',
    'Neptuno',
    'Plutón',
    'Ascendente',
    'Nodo',
    'Norte',
    'Sur',
    'Casa',
    'Alanálisis',
    'Lectura',
    'Guía',
    'IA',
    'Premium',
  ]);

  const pushCandidate = (value) => {
    const clean = String(value || '').trim();
    if (!clean) return;
    if (excludedWords.has(clean)) return;
    if (clean.length < 2) return;
    candidateNames.push(clean);
  };

  const titleMatches = titleText.match(/\bde\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu) || [];
  const houseMatches = titleText.match(/\bCasa\s+\d+\s+de\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu) || [];

  for (const match of titleMatches) {
    pushCandidate(match.replace(/^\bde\s+/u, '').trim());
  }

  for (const match of houseMatches) {
    pushCandidate(match.replace(/^.*\bde\s+/u, '').trim());
  }

  const pairPatterns = [
    /\bentre\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\s+y\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
    /\bde\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\s+y\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
    /\b([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\s+y\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
  ];

  for (const pattern of pairPatterns) {
    for (const match of rawText.matchAll(pattern)) {
      pushCandidate(match[1]);
      pushCandidate(match[2]);
    }
  }

  const singleNamePatterns = [
    /\bde\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
    /\ba\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
    /\ben\s+([A-ZÁÉÍÓÚÑ][\p{L}'’-]*)\b/gu,
  ];

  for (const pattern of singleNamePatterns) {
    for (const match of rawText.matchAll(pattern)) {
      pushCandidate(match[1]);
    }
  }

  const repeatedCapitalized = {};
  for (const match of rawText.matchAll(/\b([A-ZÁÉÍÓÚÑ][\p{L}'’-]{2,})\b/gu)) {
    const word = match[1];
    if (excludedWords.has(word)) continue;
    repeatedCapitalized[word] = (repeatedCapitalized[word] || 0) + 1;
  }

  Object.entries(repeatedCapitalized).forEach(([word, count]) => {
    if (count >= 2) pushCandidate(word);
  });

  const uniqueNames = [...new Set(candidateNames.filter(Boolean))];
  if (!uniqueNames.length) return rawText;

  const escapedNames = uniqueNames
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .sort((a, b) => b.length - a.length);

  const regex = new RegExp(`\\b(${escapedNames.join('|')})\\b`, 'gu');
  const parts = rawText.split(regex);

  return parts.map((part, index) => {
    if (!part) return null;
    if (uniqueNames.includes(part)) {
      return (
        <span key={`hl-${part}-${index}`} style={highlightedNameStyle()}>
          {part}
        </span>
      );
    }
    return <React.Fragment key={`txt-${index}`}>{part}</React.Fragment>;
  });
}

function interpretationMetaRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  };
}


function emptyBoxStyle() {
  return {
    marginTop: 8,
    padding: 18,
    borderRadius: 20,
    border: '1px dashed rgba(247,215,116,0.24)',
    background: 'linear-gradient(135deg, rgba(255,245,194,0.05), rgba(138,43,226,0.06))',
    color: 'rgba(255,255,255,0.78)',
    fontSize: 14,
    lineHeight: 1.7,
  };
}

function errorBoxStyle() {
  return {
    marginTop: 10,
    padding: 14,
    borderRadius: 18,
    background: 'rgba(255,80,80,0.10)',
    border: '1px solid rgba(255,120,120,0.26)',
    color: 'rgba(255,225,225,0.96)',
    fontSize: 13,
    lineHeight: 1.55,
  };
}

function loadingBarStyle(width = '100%') {
  return {
    height: 11,
    width,
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.18), rgba(255,255,255,0.08))',
  };
}

function sectionDividerStyle() {
  return {
    height: 1,
    background: 'linear-gradient(90deg, rgba(247,215,116,0), rgba(247,215,116,0.30), rgba(138,43,226,0.22), rgba(247,215,116,0))',
    margin: '4px 0 16px',
  };
}


function freeQuestionShellStyle() {
  return {
    marginTop: 22,
    padding: 18,
    borderRadius: 24,
    border: '1px solid rgba(247,215,116,0.20)',
    background: 'linear-gradient(180deg, rgba(255,245,194,0.07), rgba(138,43,226,0.08), rgba(255,255,255,0.03))',
    boxShadow: '0 18px 38px rgba(0,0,0,0.18), 0 0 26px rgba(247,215,116,0.05)',
    display: 'grid',
    gap: 14,
  };
}

function freeQuestionHeaderStyle() {
  return {
    display: 'grid',
    gap: 8,
  };
}

function freeQuestionBadgeStyle() {
  return {
    display: 'inline-flex',
    width: 'fit-content',
    maxWidth: 'fit-content',
    flexShrink: 0,
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    color: '#4b0082',
    background: 'linear-gradient(135deg, rgba(255,245,194,0.98), rgba(247,215,116,0.98))',
    border: '1px solid rgba(255,243,196,0.72)',
    boxShadow: '0 8px 16px rgba(218,165,32,0.18)',
  };
}

function freeQuestionTitleStyle() {
  return {
    margin: 0,
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#fff3c4',
  };
}



function freeQuestionExamplesRowStyle() {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  };
}

function freeQuestionExampleChipStyle() {
  return {
    border: '1px solid rgba(247,215,116,0.18)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff3c4',
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: 700,
    borderRadius: 999,
    padding: '9px 12px',
    cursor: 'pointer',
  };
}

function freeQuestionComposerStyle() {
  return {
    display: 'grid',
    gap: 10,
  };
}

function freeQuestionTextareaStyle() {
  return {
    width: '100%',
    minHeight: 112,
    resize: 'vertical',
    borderRadius: 18,
    border: '1px solid rgba(247,215,116,0.18)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.08))',
    color: '#fff',
    fontSize: 16,
    WebkitTextSizeAdjust: '100%',
    textSizeAdjust: '100%',
    lineHeight: 1.7,
    padding: '14px 15px',
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function freeQuestionActionsRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  };
}

function freeQuestionCountStyle() {
  return {
    fontSize: 12,
    fontWeight: 800,
    color: 'rgba(255,243,196,0.72)',
  };
}

function freeQuestionButtonStyle(disabled = false) {
  return {
    border: '1px solid rgba(255,243,196,0.78)',
    background: disabled
      ? 'linear-gradient(135deg, rgba(255,245,194,0.35), rgba(247,215,116,0.35))'
      : 'linear-gradient(135deg, rgba(255,245,194,0.98), rgba(247,215,116,0.98))',
    color: '#4b0082',
    fontSize: 13,
    fontWeight: 900,
    borderRadius: 999,
    padding: '11px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : '0 10px 20px rgba(218,165,32,0.18)',
    opacity: disabled ? 0.65 : 1,
  };
}


function freeQuestionHistoryStyle() {
  return {
    display: 'grid',
    gap: 12,
  };
}

function freeQuestionBubbleWrapStyle(isUser = false) {
  return {
    display: 'grid',
    justifyItems: isUser ? 'end' : 'start',
  };
}

function freeQuestionBubbleStyle(isUser = false) {
  return {
    width: 'min(100%, 860px)',
    padding: isUser ? '12px 14px' : '16px 16px 15px',
    borderRadius: isUser ? '20px 20px 8px 20px' : '20px 20px 20px 8px',
    border: isUser
      ? '1px solid rgba(247,215,116,0.30)'
      : '1px solid rgba(247,215,116,0.18)',
    background: isUser
      ? 'linear-gradient(135deg, rgba(247,215,116,0.18), rgba(138,43,226,0.16))'
      : 'linear-gradient(180deg, rgba(255,245,194,0.08), rgba(138,43,226,0.07), rgba(255,255,255,0.03))',
    boxShadow: '0 12px 26px rgba(0,0,0,0.16)',
    display: 'grid',
    gap: 8,
  };
}

function freeQuestionBubbleLabelStyle(isUser = false) {
  return {
    fontSize: 10,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    fontWeight: 900,
    color: isUser ? '#fff3c4' : '#f7d774',
  };
}

function freeQuestionBubbleTextStyle(isUser = false) {
  return {
    margin: 0,
    whiteSpace: 'pre-wrap',
    fontSize: isUser ? 14 : 15,
    lineHeight: isUser ? 1.65 : 1.85,
    color: isUser ? '#fff' : 'rgba(255,255,255,0.94)',
  };
}


function freeQuestionBubbleBadgeRowStyle() {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  };
}

function freeQuestionBubbleBadgeStyle(kind = 'default') {
  const isCached = kind === 'cached';
  const isFallback = kind === 'fallback';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    padding: '0 9px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: isFallback ? '#ffe8e8' : isCached ? '#4b0082' : '#fff3c4',
    border: isFallback
      ? '1px solid rgba(255,120,120,0.26)'
      : isCached
        ? '1px solid rgba(255,243,196,0.70)'
        : '1px solid rgba(247,215,116,0.20)',
    background: isFallback
      ? 'rgba(255,80,80,0.10)'
      : isCached
        ? 'linear-gradient(135deg, rgba(255,245,194,0.98), rgba(247,215,116,0.98))'
        : 'rgba(255,255,255,0.06)',
  };
}


function localizeQuestionLabel(label) {
  const raw = String(label || '').trim();
  if (!raw) return '';

  if (isEnglishPresentation()) {
    return raw
      .replace(/\bSol\b/gi, 'Sun').replace(/\bLuna\b/gi, 'Moon').replace(/\bMercurio\b/gi, 'Mercury')
      .replace(/\bMarte\b/gi, 'Mars').replace(/\bJúpiter\b/gi, 'Jupiter').replace(/\bSaturno\b/gi, 'Saturn')
      .replace(/\bUrano\b/gi, 'Uranus').replace(/\bNeptuno\b/gi, 'Neptune').replace(/\bPlutón\b/gi, 'Pluto')
      .replace(/\bAscendente\b/gi, 'Ascendant').replace(/\bMedio Cielo\b/gi, 'Midheaven')
      .replace(/\bNodo Norte\b/gi, 'North Node').replace(/\bNodo Sur\b/gi, 'South Node')
      .replace(/\bconjunción\b/gi, 'conjunction').replace(/\boposición\b/gi, 'opposition')
      .replace(/\btrígono\b/gi, 'trine').replace(/\bcuadratura\b/gi, 'square').replace(/\bsextil\b/gi, 'sextile');
  }

  return raw
    .replace(/\bsun\b/gi, 'Sol')
    .replace(/\bmoon\b/gi, 'Luna')
    .replace(/\bmercury\b/gi, 'Mercurio')
    .replace(/\bvenus\b/gi, 'Venus')
    .replace(/\bmars\b/gi, 'Marte')
    .replace(/\bjupiter\b/gi, 'Júpiter')
    .replace(/\bsaturn\b/gi, 'Saturno')
    .replace(/\buranus\b/gi, 'Urano')
    .replace(/\bneptune\b/gi, 'Neptuno')
    .replace(/\bpluto\b/gi, 'Plutón')
    .replace(/\bascendant\b/gi, 'Ascendente')
    .replace(/\basc\b/gi, 'Ascendente')
    .replace(/\bmc\b/gi, 'Medio Cielo')
    .replace(/\bnorth node\b/gi, 'Nodo Norte')
    .replace(/\bsouth node\b/gi, 'Nodo Sur')
    .replace(/\bnode\b/gi, 'Nodo');
}

function polishAnswerPreviewText(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';

  return raw
    .replace(/^mir[áa],?\s*/i, '')
    .replace(/^bueno,?\s*/i, '')
    .replace(/^en esta lectura,?\s*/i, '')
    .replace(/^ac[aá],?\s*/i, '')
    .replace(/^la conjunci[oó]n exacta de\s*/i, 'La conjunción de ')
    .trim();
}

function premiumFreeQuestionTitle(answerTitle, meta = {}) {
  const raw = String(answerTitle || '').trim();
  const question = String(meta?.question || '').trim();
  const normalizedRaw = raw.toLowerCase();

  if (raw && normalizedRaw !== 'pregunta libre sobre esta sinastría') {
    return localizeQuestionLabel(raw);
  }

  if (question) {
    const normalizedQuestion = question
      .toLowerCase()
      .replace(/^¿/, '')
      .replace(/\?$/, '')
      .trim();

    const compatibilityPatterns = [
      {
        match: /(compatib(?:le|les|ilidad).*)(venus|marte|luna|mercurio|sol|ascendente)|\b(venus|marte|luna|mercurio|sol|ascendente)\b.*compatib/i,
        titleMap: {
          venus: 'Compatibilidad entre sus Venus',
          marte: 'Compatibilidad entre sus Marte',
          luna: 'Compatibilidad entre sus Lunas',
          mercurio: 'Compatibilidad entre sus Mercurios',
          sol: 'Compatibilidad entre sus Soles',
          ascendente: 'Compatibilidad entre sus Ascendentes',
        },
      },
    ];

    for (const config of compatibilityPatterns) {
      const match = normalizedQuestion.match(config.match);
      if (match) {
        const detectedPlanet = ['venus', 'marte', 'luna', 'mercurio', 'sol', 'ascendente'].find((planet) =>
          normalizedQuestion.includes(planet)
        );
        if (detectedPlanet && config.titleMap[detectedPlanet]) {
          return config.titleMap[detectedPlanet];
        }
      }
    }

    if (normalizedQuestion.includes('venus')) return 'Dinámica afectiva entre ustedes';
    if (normalizedQuestion.includes('marte')) return 'Dinámica de deseo y acción';
    if (normalizedQuestion.includes('luna')) return 'Resonancia emocional entre ustedes';
    if (normalizedQuestion.includes('mercurio')) return 'Lenguaje mental y comunicación';
    if (normalizedQuestion.includes('sol')) return 'Encuentro entre sus identidades';
    if (normalizedQuestion.includes('ascendente')) return 'Reconocimiento e impacto mutuo';

    const cleanedQuestion = question
      .replace(/^¿/, '')
      .replace(/\?$/, '')
      .trim();

    if (cleanedQuestion) {
      const firstChar = cleanedQuestion.charAt(0).toUpperCase();
      const rest = cleanedQuestion.slice(1);
      return `Lectura libre · ${firstChar}${rest}`;
    }
  }

  return 'Lectura libre de Kronos';
}

function premiumFreeQuestionEyebrow(meta = {}) {
  if (meta?.cached) return 'Respuesta guardada';
  if (meta?.fallback) return 'Lectura especial';
  return 'Lectura libre de Kronos';
}

function categoryLabel(category) {
  const map = {
    general: synText('Núcleo del vínculo', 'Connection core'),
    houses: synText('Interacciones entre cartas', 'Chart interactions'),
    aspects: synText('Aspectos clave', 'Key aspects'),
    emotional: synText('Resonancia emocional', 'Emotional resonance'),
    communication: synText('Lenguaje del vínculo', 'Communication style'),
    attraction: synText('Campo de atracción', 'Attraction field'),
    stability: synText('Base de sostén', 'Foundation and stability'),
    karmic: synText('Eje evolutivo', 'Growth path'),
  };
  return map[String(category || '').trim()] || synText('Interpretación', 'Interpretation');
}

function helperCopy(question, asked) {
  if (asked) return synText('Ya fue explorada. Podés volver a abrir esta interpretación cuando quieras.', 'Already explored. You can reopen this interpretation at any time.');

  const category = String(question?.category || '').trim();
  if (category === 'general') return synText('Abrí una lectura sobre el motor central de esta conexión.', 'Explore the central dynamic of this connection.');
  if (category === 'emotional') return synText('Explorá cómo se vive el vínculo en la capa más sensible y afectiva.', 'Explore the connection’s emotional and sensitive layer.');
  if (category === 'communication') return synText('Descubrí cómo se entienden, traducen y procesan entre sí.', 'Discover how you understand and communicate with each other.');
  if (category === 'attraction') return synText('Leé qué sostiene la química, el magnetismo y la tensión erótica.', 'Explore the chemistry, magnetism, and desire between you.');
  if (category === 'stability') return synText('Observá qué base real de construcción y permanencia aparece acá.', 'See what supports stability and long-term growth.');
  if (category === 'karmic') return synText('Abrí una lectura sobre aprendizajes, repetición de patrones y evolución.', 'Explore lessons, recurring patterns, and growth.');
  if (category === 'aspects') return synText('Entrá en un aspecto específico para ver cómo influye en la dinámica general.', 'Open a specific aspect to see how it shapes the overall dynamic.');
  if (category === 'houses') return synText('Leé cómo una energía de una carta impacta una zona clave de la otra.', 'See how one chart’s energy affects a key area of the other chart.');
  return synText('Elegí esta pregunta para abrir una lectura guiada específica.', 'Choose this question to open a focused guided reading.');
}

function formatPriorityLabel(priority) {
  if (!priority) return '';
  if (priority >= 95) return synText('Clave', 'Key');
  if (priority >= 90) return synText('Alta', 'High');
  if (priority >= 85) return synText('Media', 'Medium');
  return `P${priority}`;
}

function renderQuestionCard({
  question,
  index,
  activeQuestionId,
  askedQuestionIds,
  loadingQuestionId,
  onAskQuestion,
}) {
  const questionId = String(question?.id || '').trim();
  const active = questionId && questionId === activeQuestionId;
  const asked = askedQuestionIds.includes(questionId);
  const loading = questionId && questionId === loadingQuestionId;
  const disabled = !questionId || Boolean(loadingQuestionId && loadingQuestionId !== questionId);

  const categoryText = categoryLabel(question?.category);

  const questionText = localizedServerContent(
    localizeQuestionLabel(question?.label),
    categoryText
  ) || synText('Pregunta guiada', 'Guided question');

  const normalizeVisibleLabel = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const showQuestionText =
    normalizeVisibleLabel(questionText) !==
    normalizeVisibleLabel(categoryText);

  return (
    <button
      key={questionId || `question-${index}`}
      type="button"
      onClick={() => {
        if (disabled || !questionId || typeof onAskQuestion !== 'function') return;
        onAskQuestion(question);
      }}
      style={cardStyle({ active, asked, disabled })}
      aria-pressed={active}
      aria-busy={loading}
      disabled={disabled}
    >
      <span style={categoryChipStyle()}>
        <span style={questionCardKronosAvatarWrapStyle()} aria-hidden>
          <img
            src="/logo/kronos/Kronos3.png"
            alt="Kronos IA"
            style={questionCardKronosAvatarImageStyle()}
          />
        </span>
        {categoryText}
      </span>
      {showQuestionText ? (
        <span style={questionTextStyle()}>
          {questionText}
        </span>
      ) : null}
      <span style={helperTextStyle()}>{helperCopy(question, asked)}</span>
      <span style={stateRowStyle()}>
        <span style={miniStateStyle(loading, active)}>
          {loading ? synText('Interpretando…', 'Interpreting…') : active ? synText('Activa', 'Active') : asked ? synText('Ya abierta', 'Already opened') : synText('Disponible', 'Available')}
        </span>
        <span style={miniStateStyle(false, false)}>{formatPriorityLabel(question?.priority)}</span>
      </span>
    </button>
  );
}

function renderLoadingGrid() {
  return (
    <div style={gridStyle()}>
      {[0, 1, 2, 3].map((item) => (
        <div key={item} style={cardStyle({ disabled: true })}>
          <div style={{ ...loadingBarStyle('40%'), height: 22, borderRadius: 999 }} />
          <div style={loadingBarStyle('92%')} />
          <div style={loadingBarStyle('76%')} />
          <div style={loadingBarStyle('68%')} />
        </div>
      ))}
    </div>
  );
}

function renderInterpretation(interpretation) {
  if (!interpretation) return null;

  return (
    <div style={interpretationCardStyle()}>
      <div style={interpretationMetaRowStyle()}>
        <div style={kronosIdentityRowStyle()}>
          <div style={kronosInlineAvatarWrapStyle(46)} aria-hidden>
            <img
              src="/logo/kronos/Kronos3.png"
              alt="Kronos IA"
              style={kronosInlineAvatarImageStyle(42)}
            />
          </div>
          <span style={interpretationEyebrowStyle()}>{synText('Lectura de Kronos IA', 'Kronos AI reading')}</span>
        </div>
        <span style={miniStateStyle(false, true)}>{synText('Lectura completa', 'Complete reading')}</span>
      </div>
      <h4 style={interpretationTitleStyle()}>
        {localizedServerContent(localizeQuestionLabel(interpretation?.title), synText('Interpretación guiada', 'Guided interpretation'))}
      </h4>
      <p style={interpretationTextStyle()}>
        {renderHighlightedAnswerText(localizedServerContent(interpretation?.answer, synText('La interpretación está disponible en tu idioma principal.', 'This guided interpretation is being prepared in English.')), localizedServerContent(interpretation?.title, ''))}
      </p>
    </div>
  );
}

function renderFreeQuestionAnswer(answer, meta = {}) {
  if (!answer || typeof answer !== 'object') return null;

  return (
    <div style={freeQuestionBubbleStyle(false)}>
      <div style={kronosResponseHeaderStyle()}>
        <div style={kronosIdentityRowStyle()}>
          <div style={kronosInlineAvatarWrapStyle(46)} aria-hidden>
            <img
              src="/logo/kronos/Kronos3.png"
              alt="Kronos IA"
              style={kronosInlineAvatarImageStyle(42)}
            />
          </div>
          <span style={freeQuestionBubbleLabelStyle(false)}>Kronos IA</span>
        </div>
        <div style={freeQuestionBubbleBadgeRowStyle()}>
          <span style={freeQuestionBubbleBadgeStyle(meta?.fallback ? 'fallback' : meta?.cached ? 'cached' : 'default')}>
            {localizedServerContent(premiumFreeQuestionEyebrow(meta), synText('Lectura libre de Kronos', 'Kronos free-form reading'))}
          </span>
        </div>
      </div>
      <h4
        style={{
          ...interpretationTitleStyle(),
          fontSize: 18,
          margin: 0,
          lineHeight: 1.4,
        }}
      >
        {localizedServerContent(premiumFreeQuestionTitle(answer?.title, meta), synText('Lectura libre de Kronos', 'Kronos free-form reading'))}
      </h4>
      <p style={{ ...freeQuestionBubbleTextStyle(false), color: 'rgba(255,255,255,0.96)' }}>
        {renderHighlightedAnswerText(localizedServerContent(answer?.answer, synText('La respuesta está disponible en tu idioma principal.', 'This answer is being prepared in English.')), localizedServerContent(answer?.title, ''))}
      </p>
    </div>
  );
}


export default function GuidedQuestionsPanel({
  questions = [],
  loadingQuestions = false,
  loadingQuestionId = '',
  activeQuestionId = '',
  askedQuestionIds = [],
  activeInterpretation = null,
  error = '',
  dailyUsage = { used: 0, limit: 10, remaining: 10 },
  freeQuestionInput = '',
  setFreeQuestionInput,
  loadingFreeQuestion = false,
  freeQuestionHistory = [],
  onAskFreeQuestion,
  onAskQuestion,
}) {
  const open = true;
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showPreviousFreeQuestions, setShowPreviousFreeQuestions] = useState(false);
  const hasQuestions = Array.isArray(questions) && questions.length > 0;
  const isCompactHeader = typeof window !== 'undefined' && window.innerWidth <= 640;
  const used = Number(dailyUsage?.used || 0);
  const limit = Number(dailyUsage?.limit || 10);
  const remaining = Number.isFinite(Number(dailyUsage?.remaining))
    ? Number(dailyUsage.remaining)
    : Math.max(0, limit - used);


  const visibleQuestions = useMemo(() => {
    if (!Array.isArray(questions)) return [];
    return showAllQuestions ? questions : questions.slice(0, 6);
  }, [questions, showAllQuestions]);

  const hiddenCount = Math.max(0, questions.length - visibleQuestions.length);

  const freeQuestionCharCount = String(freeQuestionInput || '').length;
  const freeQuestionCanSend =
    typeof onAskFreeQuestion === 'function' &&
    typeof setFreeQuestionInput === 'function' &&
    !loadingFreeQuestion &&
    freeQuestionCharCount > 0;

  const freeQuestionExamples = [
    synText('¿Qué parte de esta conexión más me cuesta sostener?', 'Which part of this connection is hardest for me to sustain?'),
    synText('¿Dónde está la tensión principal entre nosotros?', 'Where is the main tension between us?'),
    synText('¿Qué hace tan profunda esta relación?', 'What makes this relationship feel so deep?'),
  ];
      const latestFreeQuestionEntry = Array.isArray(freeQuestionHistory) && freeQuestionHistory.length > 0
    ? freeQuestionHistory[0]
    : null;

  const previousFreeQuestionEntries = Array.isArray(freeQuestionHistory)
    ? freeQuestionHistory.slice(1)
    : [];

  const previousFreeQuestionsCount = previousFreeQuestionEntries.length;


  return (
    <section style={panelShellStyle(open)}>
      <style>{`
  @keyframes kronosPulse {
    0% { transform: scale(1); filter: brightness(1); }
    50% { transform: scale(1.035); filter: brightness(1.06); }
    100% { transform: scale(1); filter: brightness(1); }
  }
`}</style>
      <div aria-hidden style={shellGlowStyle('topRight')} />
      <div aria-hidden style={shellGlowStyle('bottomLeft')} />

      <div
        style={{
          ...collapsedButtonStyle(true, isCompactHeader),
          cursor: 'default',
        }}
      >
        <div style={collapsedLeftStyle(isCompactHeader)}>
          <div style={agentOrbStyle(isCompactHeader)} aria-hidden>
            <img
              src="/logo/kronos/Kronos3.png"
              alt="Kronos IA"
              style={agentAvatarImageStyle()}
            />
          </div>
          <div style={collapsedTitleWrapStyle(isCompactHeader)}>
            <span style={agentBadgeStyle()}>Kronos IA</span>
            <span
              style={{
                fontSize: isCompactHeader ? 13 : 16,
                fontWeight: 900,
                color: '#fff3c4',
                lineHeight: isCompactHeader ? 1.08 : 1.15,
                display: '-webkit-box',
                WebkitLineClamp: isCompactHeader ? 3 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {synText('Intérprete del vínculo en Zodiacus', 'Zodiacus relationship interpreter')}
            </span>
          </div>
        </div>
        <div style={collapsedRightStyle(isCompactHeader)}>
          <div
            style={{
              display: 'flex',
              flexDirection: isCompactHeader ? 'row' : 'row',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              width: isCompactHeader ? '100%' : 'auto',
            }}
          >
            <span style={statPillStyle(true, isCompactHeader)}>{`${used} / ${limit}`}</span>
            <span style={statPillStyle(false, isCompactHeader)}>{synText(`${remaining} disponibles`, `${remaining} available`)}</span>
          </div>
        </div>
      </div>

      <div style={contentWrapStyle()}>
          <div style={introBoxStyle()}>
            <img
              src="/logo/kronos/Kronos3.png"
              alt=""
              aria-hidden="true"
              style={kronosWatermarkStyle()}
            />
            <h3 style={introTitleStyle()}>{synText('Kronos interpreta la arquitectura profunda de este vínculo', 'Kronos interprets the deep architecture of this connection')}</h3>
            <p style={introTextStyle()}>
              {synText('Kronos es el intérprete IA de Zodiacus para sinastrías: lee esta conexión desde una capa profunda, detecta los focos reales del vínculo y te ayuda a abrir, uno por uno, los puntos que explican atracción, sostén, fricción, resonancia emocional y evolución compartida.', 'Kronos is Zodiacus’s AI synastry interpreter. It reads the connection deeply, identifies its central themes, and helps you explore attraction, support, friction, emotional resonance, and shared growth.')}
            </p>
          </div>

          <div style={sectionDividerStyle()} />

          {error ? <div style={errorBoxStyle()}>{error}</div> : null}

          {loadingQuestions ? renderLoadingGrid() : null}

          {!loadingQuestions && !hasQuestions ? (
            <div style={emptyBoxStyle()}>
              {synText('Todavía no hay preguntas guiadas disponibles para esta lectura. Cuando la sinastría tenga suficiente información, acá vas a poder abrir explicaciones específicas sobre aspectos, casas, dimensiones y núcleo del vínculo.', 'No guided questions are available for this reading yet. When the synastry has enough information, you will be able to explore specific explanations about aspects, houses, dimensions, and the connection’s core.')}
            </div>
          ) : null}

          {!loadingQuestions && hasQuestions ? (
            <>
              <div style={gridStyle()}>
                {visibleQuestions.map((question, index) =>
  renderQuestionCard({
    question,
    index,
    activeQuestionId,
    askedQuestionIds,
    loadingQuestionId,
    onAskQuestion,
  })
)}
              </div>

              {hiddenCount > 0 ? (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowAllQuestions((prev) => !prev)}
                    style={{
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.05)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 800,
                      borderRadius: 999,
                      padding: '10px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    {showAllQuestions
                      ? synText('Ver menos preguntas', 'Show fewer questions')
                      : synText(`Ver ${hiddenCount} pregunta${hiddenCount === 1 ? '' : 's'} más`, `Show ${hiddenCount} more question${hiddenCount === 1 ? '' : 's'}`)}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {renderInterpretation(activeInterpretation)}

          <div style={freeQuestionShellStyle()}>
            <div style={freeQuestionHeaderStyle()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={kronosInlineAvatarWrapStyle(52)} aria-hidden>
                  <img
                    src="/logo/kronos/Kronos3.png"
                    alt="Kronos IA"
                    style={kronosInlineAvatarImageStyle(48)}
                  />
                </div>
                <span style={freeQuestionBadgeStyle()}>{synText('Habla con Kronos', 'Talk to Kronos')}</span>
              </div>
              <h3 style={freeQuestionTitleStyle()}>{synText('Hablá libremente con Kronos sobre esta conexión', 'Talk freely with Kronos about this connection')}</h3>
              
            </div>

            <div style={freeQuestionExamplesRowStyle()}>
              {freeQuestionExamples.map((example) => (
                <button
  key={example}
  type="button"
  disabled={loadingFreeQuestion}
  onClick={() => {
    if (loadingFreeQuestion) return;
    if (typeof setFreeQuestionInput === 'function') {
      setFreeQuestionInput(example);
    }
  }}
  style={{
    ...freeQuestionExampleChipStyle(),
    opacity: loadingFreeQuestion ? 0.6 : 1,
    cursor: loadingFreeQuestion ? 'not-allowed' : 'pointer',
  }}
>
                  {example}
                </button>
              ))}
            </div>

            <div style={freeQuestionComposerStyle()}>
              <label htmlFor="kronos-free-question" style={{ display: 'none' }}>
                {synText('Pregunta para Kronos', 'Question for Kronos')}
              </label>
              <textarea
                id="kronos-free-question"
                name="kronosFreeQuestion"
                value={freeQuestionInput}
                onChange={(event) => {
                  if (typeof setFreeQuestionInput === 'function') {
                    setFreeQuestionInput(event.target.value.slice(0, 280));
                  }
                }}
                onKeyDown={async (event) => {
                  if (event.key !== 'Enter' || event.shiftKey) return;
                  if (!freeQuestionCanSend) return;
                  event.preventDefault();
                  const rawValue = String(freeQuestionInput || '');
                  const trimmedValue = rawValue.trim();
                  if (!trimmedValue) return;
                  try {
                    const response = await onAskFreeQuestion(trimmedValue);
                    if (response?.ok && typeof setFreeQuestionInput === 'function') {
                      setFreeQuestionInput('');
                    }
                  } catch (err) {
                  }
                }}
                placeholder={synText('Preguntale algo a Kronos sobre este vínculo...', 'Ask Kronos something about this connection...')}
                style={freeQuestionTextareaStyle()}
              />
              <div style={freeQuestionActionsRowStyle()}>
                <span style={freeQuestionCountStyle()}>{`${freeQuestionCharCount} / 280`}</span>
                <button
                  type="button"
                  onClick={async () => {
  if (!freeQuestionCanSend) return;
  const rawValue = String(freeQuestionInput || '');
  const trimmedValue = rawValue.trim();
  if (!trimmedValue) return;
  try {
    const response = await onAskFreeQuestion(trimmedValue);
    if (response?.ok && typeof setFreeQuestionInput === 'function') {
      setFreeQuestionInput('');
    }
  } catch (err) {
  }
}}
                  disabled={!freeQuestionCanSend}
                  style={freeQuestionButtonStyle(!freeQuestionCanSend)}
                >
                  {loadingFreeQuestion ? synText('Consultando a Kronos…', 'Asking Kronos…') : synText('Preguntar a Kronos', 'Ask Kronos')}
                </button>
              </div>
            </div>

                        {latestFreeQuestionEntry ? (
              <div style={freeQuestionHistoryStyle()}>
                <div style={freeQuestionBubbleWrapStyle(true)}>
                  <div style={freeQuestionBubbleStyle(true)}>
                    <span style={freeQuestionBubbleLabelStyle(true)}>{synText('Tu pregunta', 'Your question')}</span>
                    <p style={freeQuestionBubbleTextStyle(true)}>
                      {latestFreeQuestionEntry?.question || ''}
                    </p>
                  </div>
                </div>

                <div style={freeQuestionBubbleWrapStyle(false)}>
                  {renderFreeQuestionAnswer(
                    latestFreeQuestionEntry?.answer || null,
                    latestFreeQuestionEntry || {}
                  )}
                </div>

                {previousFreeQuestionsCount > 0 ? (
                  <div style={{ marginTop: 6, display: 'grid', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setShowPreviousFreeQuestions((prev) => !prev)}
                      style={{
                        alignSelf: 'center',
                        border: '1px solid rgba(247,215,116,0.18)',
                        background: 'rgba(255,255,255,0.04)',
                        color: '#fff3c4',
                        fontSize: 12,
                        fontWeight: 800,
                        borderRadius: 999,
                        padding: '10px 14px',
                        cursor: 'pointer',
                      }}
                    >
                      {showPreviousFreeQuestions
                        ? synText('Ocultar preguntas anteriores', 'Hide previous questions')
                        : synText(`Ver ${previousFreeQuestionsCount} pregunta${previousFreeQuestionsCount === 1 ? '' : 's'} anterior${previousFreeQuestionsCount === 1 ? '' : 'es'}`, `Show ${previousFreeQuestionsCount} previous question${previousFreeQuestionsCount === 1 ? '' : 's'}`)}
                    </button>

                    {showPreviousFreeQuestions ? (
                      <div style={{ display: 'grid', gap: 12 }}>
                        {previousFreeQuestionEntries.map((item, index) => (
                          <React.Fragment key={`${item?.timestamp || index}-${index}`}>
                            <div style={freeQuestionBubbleWrapStyle(true)}>
                              <div style={{ ...freeQuestionBubbleStyle(true), opacity: 0.88 }}>
                                <span style={freeQuestionBubbleLabelStyle(true)}>
                                  {synText('Pregunta anterior', 'Previous question')}
                                </span>
                                <p style={freeQuestionBubbleTextStyle(true)}>
                                  {item?.question || ''}
                                </p>
                              </div>
                            </div>

                            <div style={freeQuestionBubbleWrapStyle(false)}>
                              <div style={{ width: 'min(100%, 860px)', opacity: 0.94 }}>
                                {renderFreeQuestionAnswer(item?.answer || null, item || {})}
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
           ) : null}
          </div>

        </div>
    </section>
  );
}
